import * as React from "react";
import { useEffect, useState } from "react";
import ToggleButton from "../commons/components/buttons/ToggleButton";
import sfetch from "../commons/utils/super-fetch";
import UrlInput from "../commons/components/form/input/UrlInput";
import SavePanel from "./SavePanel";

export interface SettingAppointmentsNotificationModel {
  notificationByEmailEnabled: boolean;
  doctorBookingSystemUrl?: string;
  changeUrl: string;
  allowAppointmentOverride: boolean;
  displayAppointmentSettings: boolean;
  displayAppointmentOverride: boolean;
  appointmentOverrideLabel: string;
}

interface SettingAppointmentsNotificationProps extends SettingAppointmentsNotificationModel {
  displayError: (message: string) => any;
}

interface SettingAppointmentsBookingNotificationState {
  notificationEnabled: boolean;
  selectedMode: Mode;
  externalBookingUrlInputValue: string;
  allowAppointmentOverride: boolean;
}

enum Mode {
  EXTERNAL_LINK = "EXTERNAL_LINK",
  EMAIL = "EMAIL",
  NONE = "NONE",
}

export default function SettingAppointmentsNotification(
  props: SettingAppointmentsNotificationProps
) {
  const initialState: SettingAppointmentsBookingNotificationState = {
    notificationEnabled:
      props.notificationByEmailEnabled ||
      (props.doctorBookingSystemUrl != null && props.doctorBookingSystemUrl.length > 0),
    selectedMode:
      (props.notificationByEmailEnabled && Mode.EMAIL) ||
      (props.doctorBookingSystemUrl && Mode.EXTERNAL_LINK) ||
      Mode.NONE,
    externalBookingUrlInputValue: props.doctorBookingSystemUrl || "",
    allowAppointmentOverride: props.allowAppointmentOverride,
  };

  const [updatedState, setUpdatedState] = useState(initialState);
  const [refState, setRefState] = useState(initialState);
  const [savePanelDisabled, setSavePanelDisabled] = useState(true);

  useEffect(() => {
    const shouldSelectMode =
      updatedState.notificationEnabled && updatedState.selectedMode === Mode.NONE;
    const shouldEnterBookingUrl =
      updatedState.notificationEnabled &&
      updatedState.selectedMode === Mode.EXTERNAL_LINK &&
      !updatedState.externalBookingUrlInputValue;
    if (updatedState == refState || shouldSelectMode || shouldEnterBookingUrl) {
      setSavePanelDisabled(true);
    } else {
      setSavePanelDisabled(false);
    }
  }, [updatedState]);

  const sendNewSetting = (): Promise<any> => {
    const notificationByEmailEnabled =
      updatedState.notificationEnabled && updatedState.selectedMode === Mode.EMAIL;
    let doctorBookingSystemUrl =
      updatedState.notificationEnabled && updatedState.selectedMode === Mode.EXTERNAL_LINK
        ? updatedState.externalBookingUrlInputValue
        : null;
    const allowAppointmentOverride = updatedState.allowAppointmentOverride;
    return sfetch(props.changeUrl, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        notificationByEmailEnabled: notificationByEmailEnabled,
        doctorBookingSystemUrl: doctorBookingSystemUrl,
        allowAppointmentOverride: allowAppointmentOverride,
      }),
    })
      .then(() => {
        setRefState(updatedState);
        setSavePanelDisabled(true);
      })
      .catch(() => {
        window.location.reload();
        props.displayError(i18n["Global.Status.technical_error"]);
      });
  };

  return (
    <>
      <div className="vertical-container">
        {props.displayAppointmentOverride && (
          <div className="setting-grid">
            <div className="item-highlight">{props.appointmentOverrideLabel}</div>
            <div className="setting-description react-form inline">
              <ToggleButton
                value={updatedState.allowAppointmentOverride}
                onChange={() =>
                  setUpdatedState({
                    ...updatedState,
                    allowAppointmentOverride: !updatedState.allowAppointmentOverride,
                  })
                }
              />
            </div>
          </div>
        )}
        {props.displayAppointmentSettings && (
          <div className="setting-grid">
            <div className="item-highlight">
              {i18n["Page.Settings.Customisation.Appointments.Option.Description"]}
            </div>
            <div className="setting-description react-form inline">
              <ToggleButton
                value={updatedState.notificationEnabled}
                onChange={(newValue) =>
                  setUpdatedState({
                    ...updatedState,
                    notificationEnabled: !updatedState.notificationEnabled,
                    selectedMode: newValue ? Mode.EMAIL : Mode.NONE,
                  })
                }
              />
              {updatedState.notificationEnabled && (
                <>
                  <p>{i18n["Page.Settings.Customisation.Appointments.Choice.Description"]}</p>
                  <div className="radio-button">
                    <input
                      type="radio"
                      name="booking-system"
                      value="external"
                      id="booking-external"
                      checked={updatedState.selectedMode === Mode.EXTERNAL_LINK}
                      onChange={() =>
                        setUpdatedState({
                          ...updatedState,
                          selectedMode: Mode.EXTERNAL_LINK,
                        })
                      }
                    />
                    <label htmlFor="booking-external">
                      {i18n["Page.Settings.Customisation.Appointments.Choice.Custom.Title"]}
                    </label>
                  </div>
                  <div className="radio-button-sub-content">
                    <div>
                      {i18n["Page.Settings.Customisation.Appointments.Choice.Custom.Content"]}
                    </div>
                    <div className="booking-input-container input-group h-flex with-margins">
                      <UrlInput
                        name="externalBookingUrl"
                        placeholder={
                          i18n[
                            "Page.Settings.Customisation.Appointments.Choice.Custom.Url.Placeholder"
                          ]
                        }
                        initialValue={updatedState.externalBookingUrlInputValue}
                        readOnly={updatedState.selectedMode !== Mode.EXTERNAL_LINK}
                        onChange={(event) => {
                          if (
                            (event.value &&
                              !event.status.invalid &&
                              event.value != updatedState.externalBookingUrlInputValue) ||
                            event.value === ""
                          ) {
                            setUpdatedState({
                              ...updatedState,
                              externalBookingUrlInputValue: event.value,
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="radio-button">
                    <input
                      type="radio"
                      name="booking-system"
                      value="email"
                      id="booking-email"
                      checked={updatedState.selectedMode === Mode.EMAIL}
                      onChange={() =>
                        setUpdatedState({
                          ...updatedState,
                          selectedMode: Mode.EMAIL,
                        })
                      }
                    />
                    <label htmlFor="booking-email">
                      {i18n["Page.Settings.Customisation.Appointments.Choice.Email.Title"]}
                    </label>
                  </div>
                  <div className="radio-button-sub-content">
                    {i18n["Page.Settings.Customisation.Appointments.Choice.Email.Content"]}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        <div className="h-flex justify-end">
          <SavePanel
            onSave={() => sendNewSetting()}
            onCancel={() => setUpdatedState(refState)}
            disabled={savePanelDisabled}
          />
        </div>
      </div>
    </>
  );
}
